import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Calendar } from "../components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Loader2, Plus, Trash2, CalendarClock, CheckCircle2, Circle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DeadlineTracker() {
  const { t } = useTranslation();
  const [deadlines, setDeadlines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [form, setForm] = useState({ title: "", description: "", due_date: "", reminder_days: 7 });

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/deadlines`, { withCredentials: true });
      setDeadlines(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  };

  const handleCreate = async () => {
    if (!form.title || !form.due_date) { toast.error("Title and date are required"); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/deadlines`, form, { withCredentials: true });
      setDeadlines((prev) => [...prev, res.data].sort((a, b) => a.due_date.localeCompare(b.due_date)));
      toast.success("Deadline added!");
      setDialogOpen(false);
      setForm({ title: "", description: "", due_date: "", reminder_days: 7 });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add deadline");
    }
    setSubmitting(false);
  };

  const handleToggle = async (dl) => {
    try {
      const res = await axios.put(
        `${API}/deadlines/${dl.deadline_id}`,
        { completed: !dl.completed },
        { withCredentials: true }
      );
      setDeadlines((prev) =>
        prev.map((d) => (d.deadline_id === dl.deadline_id ? res.data : d))
      );
    } catch { toast.error("Failed to update"); }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/deadlines/${id}`, { withCredentials: true });
      setDeadlines((prev) => prev.filter((d) => d.deadline_id !== id));
      toast.success("Deadline deleted");
    } catch { toast.error("Failed to delete"); }
  };

  const deadlineDates = deadlines.map((d) => new Date(d.due_date));

  const getDaysUntil = (dateStr) => {
    const diff = new Date(dateStr) - new Date();
    return Math.ceil(diff / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="deadline-loading">
        <Loader2 className="h-8 w-8 text-teal-600 animate-spin" />
      </div>
    );
  }

  return (
    <main className="min-h-screen bg-background" data-testid="deadline-page">
      <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-heading mb-2">{t("pages.deadlinesTitle")}</h1>
            <p className="text-base text-slate-500">{t("pages.deadlinesSubtitle")}</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-teal-700 hover:bg-teal-800 text-white font-medium" data-testid="add-deadline-btn">
                <Plus className="mr-2 h-4 w-4" /> {t("pages.addDeadline")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">{t("pages.newDeadline")}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t("pages.deadlineTitle")}</Label>
                  <Input
                    placeholder="e.g., I-485 filing deadline"
                    value={form.title}
                    onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                    data-testid="deadline-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t("pages.deadlineDesc")}</Label>
                  <Textarea
                    placeholder="Add notes..."
                    value={form.description}
                    onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                    data-testid="deadline-desc-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t("pages.dueDate")}</Label>
                  <Input
                    type="date"
                    value={form.due_date}
                    onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))}
                    data-testid="deadline-date-input"
                  />
                </div>
                <Button
                  onClick={handleCreate}
                  disabled={submitting}
                  className="w-full bg-teal-700 hover:bg-teal-800 text-white font-medium"
                  data-testid="deadline-submit-btn"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {t("pages.addDeadline")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Calendar */}
          <div className="lg:col-span-5">
            <Card className="border-slate-100" data-testid="deadline-calendar">
              <CardContent className="p-4 flex justify-center">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(d) => d && setSelectedDate(d)}
                  modifiers={{ deadline: deadlineDates }}
                  modifiersStyles={{
                    deadline: { fontWeight: "bold", color: "#0F766E", backgroundColor: "#F0FDFA", borderRadius: "50%" }
                  }}
                  className="rounded-lg"
                />
              </CardContent>
            </Card>
          </div>

          {/* Deadline list */}
          <div className="lg:col-span-7">
            {deadlines.length === 0 ? (
              <Card className="border-dashed border-2 border-slate-200" data-testid="deadline-empty">
                <CardContent className="p-12 text-center">
                  <CalendarClock className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">{t("pages.noDeadlines")}</h3>
                  <p className="text-sm text-slate-500">{t("pages.noDeadlinesDesc")}</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {deadlines.map((dl) => {
                  const days = getDaysUntil(dl.due_date);
                  const isOverdue = days < 0 && !dl.completed;
                  const isUrgent = days <= 7 && days >= 0 && !dl.completed;
                  return (
                    <Card
                      key={dl.deadline_id}
                      className={`border ${isOverdue ? "border-red-200 bg-red-50/30" : isUrgent ? "border-amber-200 bg-amber-50/30" : "border-slate-100"} transition-all duration-200`}
                      data-testid={`deadline-item-${dl.deadline_id}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          <button
                            onClick={() => handleToggle(dl)}
                            className="mt-0.5 flex-shrink-0"
                            data-testid={`deadline-toggle-${dl.deadline_id}`}
                          >
                            {dl.completed ? (
                              <CheckCircle2 className="h-5 w-5 text-teal-500" />
                            ) : (
                              <Circle className={`h-5 w-5 ${isOverdue ? "text-red-400" : isUrgent ? "text-amber-400" : "text-slate-300"}`} />
                            )}
                          </button>
                          <div className="flex-1 min-w-0">
                            <h3 className={`text-sm font-semibold ${dl.completed ? "line-through text-slate-400" : "text-slate-900"}`}>
                              {dl.title}
                            </h3>
                            {dl.description && (
                              <p className="text-xs text-slate-500 mt-1">{dl.description}</p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <Badge
                                variant="outline"
                                className={`text-xs ${
                                  dl.completed ? "border-teal-200 text-teal-600" :
                                  isOverdue ? "border-red-200 text-red-600" :
                                  isUrgent ? "border-amber-200 text-amber-600" :
                                  "border-slate-200 text-slate-500"
                                }`}
                              >
                                {dl.completed ? "Completed" :
                                 isOverdue ? `${Math.abs(days)} ${t("pages.overdue")}` :
                                 days === 0 ? t("pages.dueToday") :
                                 `${days} ${t("pages.daysLeft")}`}
                              </Badge>
                              <span className="text-xs text-slate-400">{new Date(dl.due_date).toLocaleDateString()}</span>
                            </div>
                          </div>
                          <Button
                            variant="ghost" size="sm"
                            onClick={() => handleDelete(dl.deadline_id)}
                            className="text-slate-400 hover:text-red-500 flex-shrink-0"
                            data-testid={`delete-deadline-${dl.deadline_id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </main>
  );
}
